import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import {
  COMPANY_API_SERVICE_TOKEN,
  CUSTOMER_PERSON_API_SERVICE_TOKEN,
  PERSON_API_SERVICE_TOKEN,
  SCREENING_CONFIG_API_SERVICE_TOKEN,
} from '@nk/api-lib';
import { formatOrgNr } from '@nk/company-block';
import { CompanyEntity, CustomerPersonEntity, PersonEntity } from '@nk/data-entities-lib';
import { ReportTitlePageComponent, ReportTitlePageData, SpinnerComponent } from '@nk/ui-lib';
import {
  DateFormats,
  LOADING_STATE_HANDLER_SERVICE_TOKEN,
  LoadingStateService,
  titleCaseCompanyName,
} from '@nk/utils-lib';
import { catchError, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { ScreeningEntityScreeningCasesComponent } from '../screening-entity-screening-cases/screening-entity-screening-cases.component';

interface ScreeningEntityData {
  person: PersonEntity | null;
  customerPerson: CustomerPersonEntity | null;
  company: CompanyEntity | null;
}

function getScreeningEntityTitle(data: ScreeningEntityData): string {
  if (data.customerPerson?.uuid) {
    return data.customerPerson.full_name ?? '';
  }
  if (data.company?.uuid) {
    return titleCaseCompanyName(data.company.name) ?? '';
  }
  if (data.person?.uuid) {
    return data.person.full_name ?? '';
  }
  return '';
}

function getScreeningEntitySubtitle(data: ScreeningEntityData, datePipe: DatePipe): string {
  if (data.customerPerson?.uuid) {
    return data.customerPerson.birth_date
      ? (datePipe.transform(data.customerPerson.birth_date, DateFormats.Standard) ?? '')
      : '';
  }
  if (data.company?.uuid) {
    return formatOrgNr(data.company.org_nr, data.company.org_nr_schema);
  }
  if (data.person?.uuid) {
    return data.person.birth_date ? (datePipe.transform(data.person.birth_date, DateFormats.Standard) ?? '') : '';
  }
  return '';
}

@Component({
  selector: 'nk-screening-case-report-view',
  imports: [AsyncPipe, SpinnerComponent, ScreeningEntityScreeningCasesComponent, ReportTitlePageComponent],
  providers: [DatePipe, { provide: LOADING_STATE_HANDLER_SERVICE_TOKEN, useClass: LoadingStateService }],
  templateUrl: './screening-case-report-view.component.html',
  styleUrls: ['./screening-case-report-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseReportViewComponent implements OnInit {
  public isReportLoading$!: Observable<boolean>;
  public reportTitlePageData$!: Observable<ReportTitlePageData | null>;
  public screeningConfigUuid!: string;
  public screeningEntityUuid!: string;

  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _companyService = inject(COMPANY_API_SERVICE_TOKEN);
  private _customerPersonService = inject(CUSTOMER_PERSON_API_SERVICE_TOKEN);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _datePipe = inject(DatePipe);
  private _activeRoute = inject(ActivatedRoute);

  public ngOnInit(): void {
    this.screeningConfigUuid = this._activeRoute.snapshot.params['screeningConfigUuid'] as string;
    this.screeningEntityUuid = this._activeRoute.snapshot.params['screeningEntityUuid'] as string;
    const loadingId = this.constructor.name;

    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(loadingId, true);
      this.isReportLoading$ = this._reportLoadingStateService.isReportLoading$;
    }

    const today: number = Date.now();

    const screeningEntityData$ = this._screeningConfigService.screeningEntityCache.get(this.screeningEntityUuid).pipe(
      switchMap((screeningEntity) => {
        if (!screeningEntity) {
          return of<ScreeningEntityData>({ person: null, customerPerson: null, company: null });
        }

        if (screeningEntity.entity_key === 'company' && screeningEntity.entity_uuid) {
          return this._companyService.companyCache.get(screeningEntity.entity_uuid).pipe(
            map((company) => ({ person: null, customerPerson: null, company })),
            catchError(() => of<ScreeningEntityData>({ person: null, customerPerson: null, company: null })),
          );
        }

        if (screeningEntity.entity_key === 'customer_person' && screeningEntity.customer_person_uuid) {
          return this._customerPersonService.customerPersonCache
            .get(this.screeningConfigUuid, screeningEntity.customer_person_uuid)
            .pipe(
              map((customerPerson) => ({ person: null, customerPerson, company: null })),
              catchError(() => of<ScreeningEntityData>({ person: null, customerPerson: null, company: null })),
            );
        }

        if (screeningEntity.entity_key === 'person' && screeningEntity.entity_uuid) {
          return this._personService.personCache.get(screeningEntity.entity_uuid).pipe(
            map((person) => ({ person, customerPerson: null, company: null })),
            catchError(() => of<ScreeningEntityData>({ person: null, customerPerson: null, company: null })),
          );
        }

        return of<ScreeningEntityData>({ person: null, customerPerson: null, company: null });
      }),
    );

    this.reportTitlePageData$ = combineLatest([this._appService.identityDataCache.get(), screeningEntityData$]).pipe(
      map(([identity, screeningEntityData]) => {
        const data: ReportTitlePageData = {
          headerText: extractTranslation('screening.screening_case_report.title'),
          title: getScreeningEntityTitle(screeningEntityData),
          subtitle: getScreeningEntitySubtitle(screeningEntityData, this._datePipe),
          description: null,
          footerText: this._translateService.instant(extractTranslation('report.exported_by'), {
            user: identity.app_user.name,
            date: this._datePipe.transform(today, DateFormats.DateAndTime),
          }),
        };
        return data;
      }),
      tap(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(loadingId, false);
        }
      }),
      catchError(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(loadingId, false);
        }
        return of(null);
      }),
    );
  }
}
